import type { EstimateDraft } from '../api/constructionPriceEstimatorFunctions';
import { emptyApproximateSettings, normalizeApproximateSettings, type SupportedCostRecipe } from '../api/approximateCostPlan';

export type RerunReminder = {
  revision: number;
  level: 'materials' | 'prices';
  labor_plan_provenance?: string | null;
};
export const rerunReminderMessage = (level: RerunReminder['level'], laborPlanAvailable = true): string => {
  if (level === 'prices') return 'Labor or cost settings changed. Calculate prices again to update the estimate.';
  if (!laborPlanAvailable) return 'Materials or work components changed, but a labor plan is unavailable for the current components. Calculate prices again to update the estimate.';
  return 'Materials or work components changed. Fill a new labor plan, then calculate prices again.';
};
const rerunReminderKey = (flowId: string): string => `construction-price-estimator:${flowId}:rerun-reminder-v1`;
export const readRerunReminder = (flowId: string): RerunReminder | null => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(rerunReminderKey(flowId)) ?? 'null');
    if (stored && Number.isSafeInteger(stored.revision) && stored.revision > 0
      && (stored.level === 'materials' || stored.level === 'prices')) {
      return { revision: stored.revision, level: stored.level,
        ...(typeof stored.labor_plan_provenance === 'string' || stored.labor_plan_provenance === null
          ? { labor_plan_provenance: stored.labor_plan_provenance } : {}) };
    }
  } catch (_caught) { /* Storage may be unavailable; the in-memory reminder still works. */ }
  return null;
};
const costSettingsParts = (draft: EstimateDraft) => {
  const settings = normalizeApproximateSettings(draft.approximate_cost_plan_settings) ?? emptyApproximateSettings();
  return {
    materials: JSON.stringify({ ...settings, site_staffing_roles: undefined, removed_site_staffing_role_kinds: undefined,
      labor_role_selections: undefined,
      indirect_allowance_settings: undefined, labor_plan_provenance: undefined, component_order: undefined,
      ai_planning_estimates_acknowledged: undefined, other_costs: undefined, fee_assumption: undefined }),
    prices: JSON.stringify({ site_staffing_roles: settings.site_staffing_roles,
      removed_site_staffing_role_kinds: settings.removed_site_staffing_role_kinds,
      labor_role_selections: settings.labor_role_selections,
      indirect_allowance_settings: settings.indirect_allowance_settings,
      other_costs: settings.other_costs, fee_assumption: settings.fee_assumption }),
  };
};
export const rerunImpact = (before: EstimateDraft, after: EstimateDraft): RerunReminder['level'] | null => {
  const prior = costSettingsParts(before);
  const next = costSettingsParts(after);
  if (prior.materials !== next.materials) return 'materials';
  if (prior.prices !== next.prices) return 'prices';
  return null;
};

export const isValidPricingDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const isPositiveQuantity = (value: string | null | undefined): boolean => {
  if (typeof value !== 'string' || !value.trim()) return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0;
};

/**
 * Award research remains usable without a component. Once the owner chooses a
 * supported work-package component, however, its calculation inputs must be
 * complete before the one Calculate action may start server work.
 */
export const hasCompleteSelectedRecipeInputs = (
  rawSettings: EstimateDraft['approximate_cost_plan_settings'], recipes: SupportedCostRecipe[],
): boolean => {
  const settings = normalizeApproximateSettings(rawSettings);
  if (settings?.pending_components?.length) return false;
  if (!settings?.material_components.length) return true;
  return settings.material_components.every((component) => {
    if (!isPositiveQuantity(component.quantity)) return false;
    if (component.component_key === 'softwood_lumber_2x4x16_no2') return component.unit === 'board_foot';
    if (component.component_key === '20a_125v_5_20r_self_test_gfci_receptacle') return component.unit === 'each' && component.gfci_compatibility_confirmed === true;
    if (component.component_key === '20a_125v_5_20r_standard_duplex_receptacle') return component.unit === 'each'
      && component.duplex_compatibility_confirmed === true
      && (component.duplex_color === 'brown' || component.duplex_color === 'ivory')
      && component.duplex_grade === 'industrial';
    const recipe = recipes.find((candidate) => candidate.recipe_key === component.component_key);
    // Do not strand a component whose advertised recipe changed; awards
    // remain available and the server still enforces source compatibility.
    if (!recipe || component.unit !== 'square_foot' || !recipe.allowed_units.includes(component.unit)) return true;
    if (component.component_key !== 'interior_paint_flat_latex_v1') return true;
    const coats = Number(component.paint_coat_count);
    return Number.isInteger(coats) && coats >= 1 && coats <= 10;
  });
};

/** Existing site roles must have every owner-facing input needed to price their wage proxy. */
const hasCompleteSiteStaffingInputs = (rawSettings: EstimateDraft['approximate_cost_plan_settings']): boolean => {
  const settings = normalizeApproximateSettings(rawSettings);
  // A reviewed partial labor plan may intentionally leave an optional role
  // unpriced. That cannot block independent award, material, or craft lanes.
  if (settings?.labor_plan_provenance) return true;
  return (settings?.site_staffing_roles ?? []).every((role) => (
    Boolean(role.soc_code.trim())
    && isPositiveQuantity(role.headcount)
    && isPositiveQuantity(role.shift_hours)
    && (role.average_concurrent_crew_size == null
      ? isPositiveQuantity(role.active_workdays)
      : isPositiveQuantity(role.average_concurrent_crew_size) && role.craft_scope_complete === true)
  ));
};

/** User-added WD roles must be complete before calculation; server re-verifies the classification and never trusts a browser rate. */
const hasCompleteLaborRoleInputs = (rawSettings: EstimateDraft['approximate_cost_plan_settings']): boolean => {
  const settings = normalizeApproximateSettings(rawSettings);
  return (settings?.labor_role_selections ?? []).every((role) => (
    Boolean(role.classification.trim()) && Boolean(role.classification_row_id?.trim())
    && Boolean(role.work_description.trim()) && Boolean(role.hours_basis.trim()) && isPositiveQuantity(role.person_hours)
  ));
};

export const hasRequiredPriceSearchInputs = (
  draft: EstimateDraft, recipes: SupportedCostRecipe[], siteStaffingReplacedByProposal = false,
): boolean => {
  const settings = normalizeApproximateSettings(draft.approximate_cost_plan_settings);
  const hasComponents = Boolean(settings && (settings.material_components.length
    || settings.assembly_components.length || settings.project_work_requirements.length));
  return isValidPricingDate(draft.estimate_date)
    && Boolean(draft.project_location.state?.trim() && draft.project_location.county?.trim()
      && draft.davis_bacon_construction_type)
    && Boolean(draft.construction_type.trim())
    && hasCompleteSelectedRecipeInputs(draft.approximate_cost_plan_settings, recipes)
    // Default staffing is hidden without a component; it cannot block the
    // independent award-only lane after the final component is removed.
    && (!hasComponents || siteStaffingReplacedByProposal
      || hasCompleteSiteStaffingInputs(draft.approximate_cost_plan_settings))
    && hasCompleteLaborRoleInputs(draft.approximate_cost_plan_settings);
};
